import React, { useState } from "react";
import jsPDF from "jspdf";
import * as XLSX from 'xlsx';
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';
import { makeRequest2 } from '../../../utils/makeRequest';
import { getLevyBalancesURL } from '../../../utils/urls';
import { LOGO_BASE64 } from '../../../utils/logo64';

const LevyBalancesReport = () => {
    const [selectedReport] = useState({ id: 2, name: "Levy Balances Report", description: "Latest Levy Balances" });
    const [dateRange, setDateRange] = useState({ start: "", end: "" });
    const [format, setFormat] = useState("PDF Report");
    const [generatedReport, setGeneratedReport] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showConfig, setShowConfig] = useState(false);
    const [excludeBalances, setExcludeBalances] = useState(false);
    // Add this new state near where `generatedReport` is defined
    const [showOffcanvas, setShowOffcanvas] = useState(true);


    const getTodayISO = () => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const handleGenerateReport = async () => {
        try {
            if (!selectedReport) {
                toastify("Please select a report to generate.", 'error');
                return;
            }
            setIsGenerating(true);

            const facilityId = await getItem('selectedFacilityId');
            const response = await makeRequest2(`${getLevyBalancesURL}/${facilityId}`, 'GET', {});
            if (response.success) {
                const customers = response.data.customers;
                const summary = response.data.summary;

                // Filter customers based on date range (using invoice dates within each customer)
                const filteredCustomers = customers.map(customer => {
                    const filteredInvoices = customer.invoices.filter(invoice => {
                        const invoiceDate = new Date(invoice.issueDate);
                        const startDate = new Date(dateRange.start || '1900-01-01');
                        const endDate = new Date(dateRange.end || '2100-01-01');
                        return invoiceDate >= startDate && invoiceDate <= endDate;
                    });

                    // Only include customers who have invoices in the date range
                    if (filteredInvoices.length > 0) {
                        // Sort invoices by issue date to ensure chronological order
                        const sortedInvoices = [...filteredInvoices].sort((a, b) =>
                            new Date(a.issueDate) - new Date(b.issueDate)
                        );

                        // Check if B/F is being used (any invoice has balanceBroughtForward > 0)
                        const hasBalanceBroughtForward = sortedInvoices.some(inv => (inv.balanceBroughtForward || 0) > 0);

                        let totalBalance;

                        if (hasBalanceBroughtForward) {
                            // If B/F exists, use only the latest invoice's outstanding balance
                            // This avoids double-counting since B/F carries forward previous balances
                            totalBalance = sortedInvoices[sortedInvoices.length - 1].outstandingBalance;
                        } else {
                            // If no B/F, sum all outstanding balances from unpaid/partially paid invoices
                            totalBalance = sortedInvoices.reduce((sum, invoice) => {
                                return sum + (invoice.outstandingBalance > 0 ? invoice.outstandingBalance : 0);
                            }, 0);
                        }

                        // Calculate period invoiced (sum of invoice amounts only, excluding B/F)
                        const periodInvoiced = sortedInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0);

                        // Calculate total balance brought forward for this customer
                        const totalBalanceBroughtForward = sortedInvoices.reduce((sum, inv) => sum + (inv.balanceBroughtForward || 0), 0);

                        // Total invoiced including B/F
                        const totalInvoicedWithBF = periodInvoiced + totalBalanceBroughtForward;

                        return {
                            ...customer,
                            invoices: sortedInvoices,
                            periodInvoiced: periodInvoiced, // Only new invoice amounts
                            totalBalanceBroughtForward: totalBalanceBroughtForward, // Total B/F
                            totalInvoicedWithBF: totalInvoicedWithBF, // Total including B/F
                            totalBalance: totalBalance,
                            balanceStatus: totalBalance > 0 ? 'Outstanding' :
                                totalBalance < 0 ? 'Overpayment' : 'Paid',
                        };
                    }
                    return null;
                }).filter(customer => customer !== null);

                setGeneratedReport({
                    name: selectedReport.name,
                    format,
                    dateRange,
                    summary: {
                        totalCustomers: filteredCustomers.length,
                        customersWithOverpayment: filteredCustomers.filter(c => c.totalBalance < 0).length,
                        customersWithOutstanding: filteredCustomers.filter(c => c.totalBalance > 0).length,
                        customersOverdue: filteredCustomers.filter(c => c.isOverdue).length,
                        totalOutstandingAmount: filteredCustomers.reduce((sum, c) => sum + Math.max(0, c.totalBalance), 0),
                    },
                    customers: filteredCustomers
                });
                toastify("Levy Balances Report generated successfully!", 'success');
            } else {
                throw new Error(response.error);
            }
        } catch (err) {
            console.error("Error generating report:", err);
            toastify(err.message, 'error');
            setGeneratedReport(null);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownload = () => {
        if (!generatedReport) {
            toastify("No report to download.", 'error');
            return;
        }

        const { name, format, customers, summary } = generatedReport;

        if (!customers || customers.length === 0) {
            toastify("No customer data to export.", 'error');
            return;
        }

        // Sanitize filename (remove invalid characters for most OS)
        const safeName = name.replace(/[<>:"/\\|?*\s]+/g, '_');
        const filename = `${safeName}_${new Date().toISOString().slice(0, 10)}`;

        let blob;

        switch (format) {
            case "CSV Data": {
                const csvHeader = "Customer,Unit,Balance Status,Total Invoiced (B/F),Period Invoiced,Total Balance\n";
                const csvData = customers.map(customer => [
                    `"${customer.customer.fullName}"`,
                    `"${customer.unit?.name || 'N/A'}"`,
                    `"${customer.balanceStatus}"`,
                    customer.totalInvoicedWithBF,
                    customer.periodInvoiced,
                    customer.totalBalance
                ].join(',')).join('\n');

                blob = new Blob([csvHeader + csvData], { type: 'text/csv' });
                break;
            }

            case "JSON Data": {
                const jsonData = JSON.stringify(customers, null, 2);
                blob = new Blob([jsonData], { type: 'application/json' });
                break;
            }

            case "PDF Report": {
                const generatePdf = async () => {
                    const doc = new jsPDF();
                    const currentDate = formatDate(new Date());
                    const facilityName = await getItem("selectedFacilityName");

                    const pageWidth = doc.internal.pageSize.getWidth();
                    const pageHeight = doc.internal.pageSize.getHeight();

                    const logoBase64 = LOGO_BASE64;
                    if (logoBase64) {
                        doc.addImage(logoBase64, "PNG", 14, 10, 40, 20);
                    }

                    // Company info (top right)
                    doc.setFontSize(12).setFont("helvetica", "bold").setTextColor(0, 78, 152);
                    doc.text("Payserve Ltd", 195, 15, { align: "right" });
                    doc.setFontSize(9).setFont("helvetica", "normal").setTextColor(0, 0, 0);
                    doc.text("Nairobi, Kenya.", 195, 22, { align: "right" });
                    doc.text("https://payserve.co.ke/", 195, 27, { align: "right" });
                    doc.text("Email: info@payserve.com", 195, 32, { align: "right" });

                    // Title
                    doc.setFontSize(16).setFont("helvetica", "bold").setTextColor(0, 78, 152);
                    const reportTitle = facilityName
                        ? `${facilityName} - ${generatedReport.name}`
                        : generatedReport.name;
                    doc.text(reportTitle, 14, 45);

                    // Date
                    doc.setFontSize(10).text(`Date: ${currentDate}`, 14, 55);

                    // SUMMARY
                    doc.setFontSize(10).setFont("helvetica", "bold").setTextColor(0, 78, 152);
                    doc.text("SUMMARY", 14, 65);

                    doc.setFont("helvetica", "normal").setTextColor(0, 0, 0);
                    let summaryY = 75;
                    doc.text(`Total Customers: ${summary.totalCustomers}`, 14, summaryY);
                    summaryY += 5;
                    doc.text(`Customers with Overpayment: ${summary.customersWithOverpayment}`, 14, summaryY);
                    summaryY += 5;
                    doc.text(`Customers with Outstanding: ${summary.customersWithOutstanding}`, 14, summaryY);
                    summaryY += 5;
                    doc.text(`Customers Overdue: ${summary.customersOverdue}`, 14, summaryY);
                    summaryY += 5;
                    doc.text(`Total Outstanding Amount: ${summary.totalOutstandingAmount.toLocaleString()}`, 14, summaryY);
                    summaryY += 10;

                    // CUSTOMER BALANCES
                    doc.setFont("helvetica", "bold").setTextColor(0, 78, 152);
                    doc.text("CUSTOMER BALANCES", 14, summaryY);
                    summaryY += 10;

                    const tableColumn = [
                        "Customer",
                        "Unit",
                        "Status",
                        "Total Invoiced (B/F)",
                        "Period Invoiced",
                        "Total Balance"
                    ];
                    const tableRows = customers.map((customer) => [
                        customer.customer.fullName,
                        customer.unit?.name || "N/A",
                        customer.balanceStatus,
                        customer.totalInvoicedWithBF.toLocaleString(),
                        customer.periodInvoiced.toLocaleString(),
                        customer.totalBalance.toLocaleString()
                    ]);

                    doc.autoTable({
                        startY: summaryY,
                        head: [tableColumn],
                        body: tableRows,
                        theme: "striped",
                        styles: { fontSize: 8 },
                        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                        margin: { left: 14, right: 14 },
                        didDrawPage: (data) => {
                            const pageCount = doc.internal.getNumberOfPages();
                            const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;

                            doc.setFontSize(9);
                            doc.setTextColor(150);
                            doc.text(
                                "Powered by Payserve Ltd",
                                pageWidth / 2,
                                pageHeight - 10,
                                { align: "center" }
                            );
                            doc.text(
                                `Page ${pageCurrent} of ${pageCount}`,
                                pageWidth - 20,
                                pageHeight - 10,
                                { align: "right" }
                            );
                        }
                    });

                    let currentY = doc.lastAutoTable.finalY + 20;

                    if (!excludeBalances) {
                        customers.forEach((customer) => {
                            if (customer.invoices && customer.invoices.length > 0) {
                                if (currentY > 250) {
                                    doc.addPage();
                                    currentY = 20;
                                }
                                doc.setFontSize(12).setFont("helvetica", "bold").setTextColor(0, 78, 152);
                                doc.text(`${customer.customer.fullName} - Invoice Details`, 14, currentY);
                                currentY += 10;

                                const invoiceColumns = [
                                    "Invoice #",
                                    "Description",
                                    "Issue Date",
                                    "B/F",
                                    "Amount",
                                    "Total Due",
                                    "Paid",
                                    "Outstanding",
                                    "Status"
                                ];
                                const invoiceRows = customer.invoices.map((invoice) => [
                                    invoice.invoiceNumber,
                                    invoice.description,
                                    new Date(invoice.issueDate).toLocaleDateString("en-GB"),
                                    (invoice.balanceBroughtForward || 0).toLocaleString(),
                                    invoice.totalAmount.toLocaleString(),
                                    ((invoice.balanceBroughtForward || 0) + invoice.totalAmount).toLocaleString(),
                                    invoice.amountPaid.toLocaleString(),
                                    invoice.outstandingBalance.toLocaleString(),
                                    invoice.status
                                ]);

                                doc.autoTable({
                                    startY: currentY,
                                    head: [invoiceColumns],
                                    body: invoiceRows,
                                    theme: "striped",
                                    styles: { fontSize: 7 },
                                    headStyles: { fillColor: [52, 152, 219], textColor: 255 },
                                    margin: { left: 14, right: 14 },
                                    didDrawPage: (data) => {
                                        const pageCount = doc.internal.getNumberOfPages();
                                        const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;

                                        doc.setFontSize(9);
                                        doc.setTextColor(150);
                                        doc.text(
                                            "Powered by Payserve Ltd",
                                            pageWidth / 2,
                                            pageHeight - 10,
                                            { align: "center" }
                                        );
                                        doc.text(
                                            `Page ${pageCurrent} of ${pageCount}`,
                                            pageWidth - 20,
                                            pageHeight - 10,
                                            { align: "right" }
                                        );
                                    }
                                });

                                currentY = doc.lastAutoTable.finalY + 15;
                            }
                        });
                    }

                    doc.save(`${filename}.pdf`);
                    toastify("PDF report downloaded successfully!", "success");
                };
                generatePdf();
                return;
            }

            case "Excel Spreadsheet": {
                // Customer summary sheet
                const customerData = customers.map(customer => ({
                    "Customer": customer.customer.fullName,
                    "Unit": customer.unit?.name || 'N/A',
                    "Balance Status": customer.balanceStatus,
                    "Total Invoiced (B/F)": customer.totalInvoicedWithBF,
                    "Period Invoiced": customer.periodInvoiced,
                    "Total Balance": customer.totalBalance
                }));

                // Invoice details sheet
                const invoiceData = [];
                customers.forEach(customer => {
                    customer.invoices.forEach(invoice => {
                        invoiceData.push({
                            "Customer": customer.customer.fullName,
                            "Invoice Number": invoice.invoiceNumber,
                            "Description": invoice.description,
                            "Issue Date": new Date(invoice.issueDate).toLocaleDateString("en-GB"),
                            "Balance Brought Forward": invoice.balanceBroughtForward || 0,
                            "Invoice Amount": invoice.totalAmount,
                            "Total Due": (invoice.balanceBroughtForward || 0) + invoice.totalAmount,
                            "Amount Paid": invoice.amountPaid,
                            "Outstanding Balance": invoice.outstandingBalance,
                            "Status": invoice.status
                        });
                    });
                });

                // Create workbook with multiple sheets
                const workbook = XLSX.utils.book_new();

                // Customer summary sheet
                const customerSheet = XLSX.utils.json_to_sheet(customerData);
                XLSX.utils.book_append_sheet(workbook, customerSheet, "Customer Balances");

                // Invoice details sheet
                if (invoiceData.length > 0) {
                    const invoiceSheet = XLSX.utils.json_to_sheet(invoiceData);
                    XLSX.utils.book_append_sheet(workbook, invoiceSheet, "Invoice Details");
                }

                XLSX.writeFile(workbook, `${filename}.xlsx`);
                toastify("Excel spreadsheet downloaded successfully!", 'success');
                return;
            }

            default:
                toastify("Unsupported format.", 'error');
                return;
        }

        if (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${filename}.${format.split(' ')[0].toLowerCase()}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toastify("Download started!", 'success');
        }
    };

    return (
        <div>
            <div className="card shadow-sm h-100">
                <div className="card-body">
                    <h5 className="card-title">{selectedReport.name}</h5>
                    <p className="card-text text-muted">{selectedReport.description}</p>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowConfig(!showConfig)}
                    >
                        {showConfig ? "Hide Configuration" : "Generate Report"}
                    </button>

                    {showConfig && (
                        <div className="mt-4">
                            <div className="card shadow-sm col-sm-6">
                                <div className="card-header">
                                    <h6 className="mb-0">
                                        <i className="bi bi-gear me-2"></i>
                                        {selectedReport.name} Configuration
                                    </h6>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Export Format</label>
                                        <select
                                            className="form-select"
                                            value={format}
                                            onChange={(e) => setFormat(e.target.value)}
                                        >
                                            <option>PDF Report</option>
                                            <option>Excel Spreadsheet</option>
                                            <option>CSV Data</option>
                                            <option>JSON Data</option>
                                        </select>
                                    </div>

                                    <div className="mb-3 form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            id="excludeBalances"
                                            checked={excludeBalances}
                                            onChange={(e) => setExcludeBalances(e.target.checked)}
                                        />
                                        <label className="form-check-label ms-1" htmlFor="excludeBalances">
                                            Don’t Include Individual Balance Reports
                                        </label>
                                    </div>

                                    <div className="d-flex justify-content-between">
                                        <button
                                            className="btn btn-primary me-2"
                                            onClick={handleGenerateReport}
                                            disabled={isGenerating}
                                        >
                                            {isGenerating ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-play-fill me-2"></i>
                                                    Generate Report
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* {generatedReport && (
                <div
                    className="offcanvas offcanvas-end show"
                    tabIndex="-1"
                    style={{ visibility: "visible", width: "320px" }}
                >
                    <div className="offcanvas-header bg-primary text-white">
                        <h5 className="offcanvas-title">Report Ready</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setGeneratedReport(null)}
                        ></button>
                    </div>
                    <div className="offcanvas-body">
                        <p>
                            <strong>{generatedReport.name}</strong> generated successfully.
                        </p>
                        <p>
                            Format: <strong>{generatedReport.format}</strong>
                        </p>
                        <p>
                            Date: {new Date().toLocaleDateString('en-GB')}
                        </p>

                        <button
                            className="btn btn-success w-100 mb-2"
                            onClick={handleDownload}
                        >
                            <i className="bi bi-download me-2"></i>Download
                        </button>
                        <button
                            className="btn btn-outline-secondary w-100"
                            onClick={() => setGeneratedReport(null)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )} */}

            {generatedReport && showOffcanvas && (
                <div
                    className="offcanvas offcanvas-end show"
                    tabIndex="-1"
                    style={{ visibility: "visible", width: "320px" }}
                >
                    <div className="offcanvas-header bg-primary text-white">
                        <h5 className="offcanvas-title">Report Ready</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setShowOffcanvas(false)} // 👈 closes panel only
                        ></button>
                    </div>
                    <div className="offcanvas-body">
                        <p>
                            <strong>{generatedReport.name}</strong> generated successfully.
                        </p>
                        <p>
                            Format: <strong>{generatedReport.format}</strong>
                        </p>
                        <p>Date: {new Date().toLocaleDateString('en-GB')}</p>

                        <button
                            className="btn btn-success w-100 mb-2"
                            onClick={handleDownload}
                        >
                            <i className="bi bi-download me-2"></i>Download
                        </button>
                        <button
                            className="btn btn-outline-secondary w-100"
                            onClick={() => setShowOffcanvas(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* 👇 This button appears when panel is hidden */}
            {generatedReport && !showOffcanvas && (
                <button
                    className="btn btn-primary position-fixed"
                    style={{ top: '1rem', right: '1rem', zIndex: 1050 }}
                    onClick={() => setShowOffcanvas(true)}
                >
                    <i className="bi bi-eye me-2"></i>Show Report Panel
                </button>
            )}

            {generatedReport && (
                <div className="mt-4">
                    <h5>Summary</h5>
                    <ul>
                        <li>Total Customers: {generatedReport.summary.totalCustomers}</li>
                        <li>Customers with Overpayment: {generatedReport.summary.customersWithOverpayment}</li>
                        <li>Customers with Outstanding: {generatedReport.summary.customersWithOutstanding}</li>
                        <li>Customers Overdue: {generatedReport.summary.customersOverdue}</li>
                        <li>Total Outstanding Amount: {generatedReport.summary.totalOutstandingAmount.toLocaleString()}</li>
                    </ul>

                    <h6 className="mt-3">Customer Balances</h6>
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Customer</th>
                                <th>Unit</th>
                                <th>Balance Status</th>
                                <th>Total Invoiced (B/F)</th>
                                <th>Period Invoiced</th>
                                <th>Current Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {generatedReport.customers.map((customer) => (
                                <tr key={customer.customerId}>
                                    <td>{customer.customer.fullName}</td>
                                    <td>{customer.unit?.name || 'N/A'}</td>
                                    <td>
                                        <span className={`badge ${customer.balanceStatus === 'Outstanding' ? 'bg-warning text-light' :
                                            customer.balanceStatus === 'Overpayment' ? 'bg-info text-light' : 'bg-success text-light'
                                            }`}>
                                            {customer.balanceStatus}
                                        </span>
                                    </td>
                                    <td>{customer.totalInvoicedWithBF.toLocaleString()}</td>
                                    <td>{customer.periodInvoiced.toLocaleString()}</td>
                                    <td className={customer.totalBalance > 0 ? 'text-danger' : customer.totalBalance < 0 ? 'text-info' : 'text-success'}>
                                        {customer.totalBalance.toLocaleString()}
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <h6 className="mt-3">Invoice Details</h6>
                    <div className="accordion" id="customerInvoicesAccordionLevy">
                        {generatedReport.customers.map((customer, customerIndex) => (
                            <div className="accordion-item" key={customer.customerId}>
                                <h2 className="accordion-header">
                                    <button
                                        className="accordion-button collapsed"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target={`#collapseLevy-${customerIndex}`}
                                        aria-expanded="false"
                                    >
                                        {customer.customer.fullName} - {customer.invoices.length} invoice(s)
                                    </button>
                                </h2>
                                <div
                                    id={`collapseLevy-${customerIndex}`}
                                    className="accordion-collapse collapse"
                                    data-bs-parent="#customerInvoicesAccordionLevy"
                                >
                                    <div className="accordion-body">
                                        <table className="table table-sm">
                                            <thead>
                                                <tr>
                                                    <th>Invoice #</th>
                                                    <th>Description</th>
                                                    <th>Issue Date</th>
                                                    <th>B/F</th>
                                                    <th>Amount</th>
                                                    <th>Total Due</th>
                                                    <th>Paid</th>
                                                    <th>Outstanding</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customer.invoices.map((invoice) => (
                                                    <tr key={invoice.invoiceId}>
                                                        <td>{invoice.invoiceNumber}</td>
                                                        <td>{invoice.description}</td>
                                                        <td>{new Date(invoice.issueDate).toLocaleDateString("en-GB")}</td>
                                                        <td className="text-warning">{(invoice.balanceBroughtForward || 0).toLocaleString()}</td>
                                                        <td>{invoice.totalAmount.toLocaleString()}</td>
                                                        <td className="fw-bold">{((invoice.balanceBroughtForward || 0) + invoice.totalAmount).toLocaleString()}</td>
                                                        <td>{invoice.amountPaid.toLocaleString()}</td>
                                                        <td className={invoice.outstandingBalance > 0 ? 'text-danger' : 'text-success'}>
                                                            {invoice.outstandingBalance.toLocaleString()}
                                                        </td>
                                                        <td>{invoice.status}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LevyBalancesReport;